import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { PrimaryBlueButton, SecondaryBlueButton } from "@velaro/velaro-component-lib";
import LoadingSpinner from "@/components/LoadingSpinner";
import useApi from "@/hooks/useApi";

type Step = 1 | 2 | 3;

interface TestResult {
  valid: boolean;
  storeName?: string;
  edition?: string;
  version?: string;
  error?: string;
}

export default function MagentoCreate() {
  const navigate = useNavigate();
  const api = useApi();

  const [step, setStep] = useState<Step>(1);
  const [displayName, setDisplayName] = useState("");
  const [storeUrl, setStoreUrl] = useState("");
  const [accessToken, setAccessToken] = useState("");
  const [testResult, setTestResult] = useState<TestResult | null>(null);
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleTestConnection() {
    if (!storeUrl.trim()) { setError("Enter your Magento store URL."); return; }
    if (!accessToken.trim()) { setError("Enter your Magento access token."); return; }
    setTesting(true);
    setError(null);
    setTestResult(null);
    try {
      const resp = await api.admin.post("Magento/Configs/Test", {
        storeUrl: storeUrl.trim(),
        accessToken: accessToken.trim()
      });
      const data = await resp.json() as TestResult;
      setTestResult(data);
      if (data.valid) {
        if (!displayName) setDisplayName(data.storeName || "Magento Store");
        setStep(2);
      } else {
        setError(data.error || "Connection test failed. Check your credentials.");
      }
    } catch {
      setError("Failed to test connection. Please try again.");
    } finally {
      setTesting(false);
    }
  }

  async function handleSave() {
    setSaving(true);
    setError(null);
    try {
      const resp = await api.admin.post("Magento/Configs", {
        displayName: displayName.trim() || testResult?.storeName || "Magento Store",
        storeUrl: storeUrl.trim(),
        accessToken: accessToken.trim()
      });

      if (!resp.ok) {
        const body = await resp.json().catch(() => ({})) as { error?: string };
        setError(body?.error ?? "Failed to save. Please try again.");
        return;
      }

      setStep(3);
    } catch {
      setError("An unexpected error occurred.");
    } finally {
      setSaving(false);
    }
  }

  const steps = [
    { n: 1, label: "Connect" },
    { n: 2, label: "Settings" },
    { n: 3, label: "Done" }
  ];

  return (
    <div className="max-w-2xl mx-auto">
      {/* Step indicator */}
      <div className="flex items-center gap-0 mb-8">
        {steps.map((s, i) => (
          <React.Fragment key={s.n}>
            <div className="flex items-center gap-2">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${step >= s.n ? "bg-cornflower-blue-500 text-white" : "bg-slate-200 text-slate-500"}`}>
                {step > s.n ? "\u2713" : s.n}
              </div>
              <span className={`text-sm ${step === s.n ? "font-semibold text-slate-800" : "text-slate-400"}`}>{s.label}</span>
            </div>
            {i < steps.length - 1 && <div className="flex-1 h-px bg-slate-200 mx-3" />}
          </React.Fragment>
        ))}
      </div>

      {/* Step 1: Enter credentials */}
      {step === 1 && (
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h2 className="text-lg font-semibold text-slate-800 mb-1">Connect your Magento store</h2>
          <p className="text-sm text-slate-500 mb-6">
            Create an Integration in your Magento admin panel and enter your Store URL and Access Token below.
          </p>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 text-sm text-blue-800 space-y-2">
            <p className="font-medium">How to get your Magento API credentials:</p>
            <ol className="list-decimal list-inside space-y-1 text-xs">
              <li>Go to <strong>Magento Admin &rarr; System &rarr; Integrations</strong></li>
              <li>Click <strong>Add New Integration</strong></li>
              <li>Give it a name (e.g. "Velaro AI Chat")</li>
              <li>Under <strong>API</strong> tab, select <strong>Resource Access: All</strong> (or grant: Customers, Sales, Catalog, Cart individually)</li>
              <li>Click <strong>Save &amp; Activate</strong> &mdash; copy the <strong>Access Token</strong></li>
            </ol>
            <p className="text-xs text-blue-600">Works with Magento 2.4+ (Open Source) and Adobe Commerce.</p>
          </div>

          <label className="flex flex-col gap-1 mb-4">
            <span className="text-sm font-medium">Store URL</span>
            <input
              type="url"
              className="input font-mono text-sm"
              placeholder="https://mystore.example.com"
              value={storeUrl}
              onChange={(e) => setStoreUrl(e.target.value)}
              autoFocus
            />
            <span className="text-xs text-slate-400">Your Magento storefront URL (e.g. https://mystore.example.com)</span>
          </label>

          <label className="flex flex-col gap-1 mb-4">
            <span className="text-sm font-medium">Access Token</span>
            <input
              type="password"
              className="input font-mono text-sm"
              placeholder="\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022"
              value={accessToken}
              onChange={(e) => setAccessToken(e.target.value)}
            />
            <span className="text-xs text-slate-400">The Integration access token from your Magento admin. Never share this.</span>
          </label>

          {error && <p className="text-red-600 text-sm mb-4">{error}</p>}

          <div className="flex justify-end gap-2">
            <SecondaryBlueButton label="Cancel" onClick={() => navigate("/integrations")} />
            {testing ? (
              <div className="flex items-center gap-2 text-sm text-slate-500"><LoadingSpinner /> Testing connection...</div>
            ) : (
              <PrimaryBlueButton label="Test & Continue" onClick={handleTestConnection} />
            )}
          </div>
        </div>
      )}

      {/* Step 2: Settings */}
      {step === 2 && testResult?.valid && (
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
              <span className="text-green-600 text-lg">{"\u2713"}</span>
            </div>
            <div>
              <div className="font-semibold text-slate-800">Connected to Magento</div>
              <div className="text-sm text-slate-500">
                Store: {testResult.storeName}
                {testResult.edition && <span className="ml-2 text-xs bg-slate-100 px-1.5 py-0.5 rounded">{testResult.edition === "commerce" ? "Adobe Commerce" : "Open Source"}</span>}
              </div>
            </div>
          </div>

          <label className="flex flex-col gap-1 mb-6">
            <span className="text-sm font-medium">Display Name</span>
            <input
              className="input"
              placeholder="e.g. Production Store"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
            />
            <span className="text-xs text-slate-400">How this connection appears in Velaro.</span>
          </label>

          {error && <p className="text-red-600 text-sm mb-4">{error}</p>}

          <div className="flex justify-between">
            <SecondaryBlueButton label="Back" onClick={() => setStep(1)} />
            {saving ? (
              <div className="flex items-center gap-2 text-sm text-slate-500"><LoadingSpinner /> Saving...</div>
            ) : (
              <PrimaryBlueButton label="Save & Finish" onClick={handleSave} />
            )}
          </div>
        </div>
      )}

      {/* Step 3: Done */}
      {step === 3 && (
        <div className="bg-white rounded-xl border border-slate-200 p-6 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-green-500 text-3xl">{"\u2713"}</span>
          </div>
          <h2 className="text-xl font-semibold text-slate-800 mb-2">Magento connected!</h2>
          <p className="text-sm text-slate-500 mb-6">
            Your Magento store is now linked to Velaro. AI workflow nodes can query customers, orders, and products automatically.
          </p>
          <div className="flex justify-center gap-3">
            <SecondaryBlueButton label="Back to Integrations" onClick={() => navigate("/integrations")} />
            <PrimaryBlueButton label="Manage Magento" onClick={() => navigate("/integrations/magento")} />
          </div>
        </div>
      )}
    </div>
  );
}
